import React, { RefObject } from "react";
import { Row } from "@tanstack/react-table";
import { TableCell } from "./TableCell";
import { ColumnStats } from "./useTableInstance";
import { useTruncatedCellTooltip } from "../hooks/useTruncatedCellTooltip";
import styles from "../styles/ReactTable.scss";

type StickyColumnsInfo = {
  selectColumnWidth: number;
  firstColumnWidth: number;
  hasSelectColumn: boolean;
  hasStickyFirstColumn: boolean;
};

type TableBodyProps<T> = {
  rows: Row<T>[];
  parentRef: RefObject<HTMLDivElement>;
  virtualRows: { index: number; size: number; start: number }[];
  totalSize: number;
  tableWidth: number;
  height: number | "100%";
  onScroll?: (scrollLeft: number) => void;
  stickyColumnsInfo: StickyColumnsInfo;
  getCellHighlightStatus?: (
    rowId: string,
    columnId: string
  ) => {
    isMatch: boolean;
    isCurrentMatch: boolean;
  };
  searchQuery?: string;
  columnStats?: Record<string, ColumnStats>;
};

const NOOP = () => {};

function handleRowClick(e: React.MouseEvent<HTMLTableRowElement>, row: any) {
  const target = e.target as HTMLElement;

  // Don't select if clicking on a link or inside a link
  if (target.closest("a")) {
    return;
  }

  if (row.getCanSelect()) {
    row.toggleSelected();
  }
}

export function TableBody<T>({
  rows,
  parentRef,
  virtualRows,
  totalSize,
  tableWidth,
  height,
  onScroll = NOOP,
  stickyColumnsInfo,
  getCellHighlightStatus = () => ({ isMatch: false, isCurrentMatch: false }),
  searchQuery = "",
  columnStats = {},
}: TableBodyProps<T>) {
  const { truncatedCellId, handleMouseEnter } = useTruncatedCellTooltip();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    onScroll(e.currentTarget.scrollLeft);
  };

  if (totalSize === 0) {
    return (
      <div className={styles.emptyState} style={{ height }}>
        There are no rows to display.
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className={styles.virtualScroll}
      style={{ height }}
      onScroll={handleScroll}
    >
      <div style={{ height: `${totalSize}px`, position: "relative" }}>
        <table className={styles.table} style={{ width: tableWidth }}>
          <tbody>
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              const isSelected = row.getIsSelected();

              return (
                <tr
                  key={row.id}
                  className={
                    isSelected ? styles.selectedRow : styles.unselectedRow
                  }
                  style={{
                    position: "absolute",
                    top: 0,
                    transform: `translateY(${virtualRow.start}px)`,
                    height: `${virtualRow.size}px`,
                    width: tableWidth,
                    display: "table",
                    tableLayout: "fixed",
                    cursor: row.getCanSelect() ? "pointer" : "default",
                  }}
                  onClick={(e) => handleRowClick(e, row)}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      key={cell.id}
                      cell={cell}
                      index={index}
                      stickyColumnsInfo={stickyColumnsInfo}
                      isTruncated={cell.id === truncatedCellId}
                      onMouseEnterOrMove={(e) => handleMouseEnter(e, cell.id)}
                      getCellHighlightStatus={getCellHighlightStatus}
                      searchQuery={searchQuery}
                      columnStats={columnStats}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
